import React, { useState, useEffect } from 'react';
import {
  Card, Typography, Button, Modal, Form, Input, Table, Space, Tag, Tabs,
  message, Popconfirm, List, Row, Col, Select, Badge, Tooltip, Switch
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, CopyOutlined,
  PlayCircleOutlined, SaveOutlined, ExportOutlined, ImportOutlined,
  AppstoreOutlined, UnorderedListOutlined, SettingOutlined,
  BulbOutlined, LoadingOutlined, QuestionCircleOutlined
} from '@ant-design/icons';
import { getApiBaseUrl } from '../config/api';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// 业务流程类型定义
interface ProcessStep {
  id: string;
  name: string;
  entities: string[];
  actions: string[];
  description?: string;
  required?: boolean;
}

interface BusinessProcess {
  id: string;
  name: string;
  description: string;
  category?: string;
  steps: ProcessStep[];
  status?: 'active' | 'draft' | 'archived';
  createdAt?: string;
  updatedAt?: string;
}

const BusinessProcessManager: React.FC = () => {
  const [processes, setProcesses] = useState<BusinessProcess[]>([]);
  const [selectedProcess, setSelectedProcess] = useState<BusinessProcess | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isStepModalVisible, setIsStepModalVisible] = useState(false);
  const [editingProcess, setEditingProcess] = useState<BusinessProcess | null>(null);
  const [editingStep, setEditingStep] = useState<ProcessStep | null>(null);
  const [form] = Form.useForm();
  const [stepForm] = Form.useForm();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isExtracting, setIsExtracting] = useState(false);

  // 默认流程（从InteractiveDesignTutorial中提取）
  const defaultProcesses: BusinessProcess[] = [
    {
      id: 'create-order',
      name: '创建订单流程',
      description: '从客户需求到订单生成的完整流程',
      category: '订单管理',
      status: 'active',
      steps: [
        {
          id: 'step1',
          name: '客户需求确认',
          entities: ['Customer', 'Contact', 'ServiceRequirement'],
          actions: ['选择客户', '确认联系人', '记录需求'],
          required: true
        },
        {
          id: 'step2',
          name: '货物信息录入',
          entities: ['Cargo', 'HSCode', 'DangerousGoods'],
          actions: ['录入货物信息', '选择HS编码', '标记危险品属性'],
          required: true
        },
        {
          id: 'step3',
          name: '路线与服务选择',
          entities: ['Route', 'Service', 'Carrier'],
          actions: ['选择运输路线', '选择服务类型', '分配承运商']
        },
        {
          id: 'step4',
          name: '费用计算与报价',
          entities: ['Quotation', 'ChargeItem', 'Currency'],
          actions: ['计算运费', '添加附加费', '生成报价单']
        },
        {
          id: 'step5',
          name: '订单生成',
          entities: ['Order', 'OrderItem', 'OrderStatus'],
          actions: ['创建订单', '关联客户和货物', '设置初始状态'],
          required: true
        }
      ]
    },
    {
      id: 'customer-master',
      name: '客户主数据管理流程',
      description: '客户信息的创建、审核和维护流程',
      category: '主数据管理',
      status: 'active',
      steps: [
        {
          id: 'step1',
          name: '总部信息创建',
          entities: ['CustomerHeadquarters', 'UnifiedCreditCode'],
          actions: ['录入统一信用码', '填写公司名称', '设置国家地区'],
          required: true
        },
        {
          id: 'step2',
          name: '部门客商分配',
          entities: ['DepartmentCustomer', 'Department', 'BusinessLine'],
          actions: ['分配到部门', '设置客户类型', '关联业务线']
        },
        {
          id: 'step3',
          name: '扩展信息配置',
          entities: ['CustomerExtension', 'Contact', 'BankAccount'],
          actions: ['添加联系人', '配置银行账号', '设置结算信息']
        },
        {
          id: 'step4',
          name: '信用与风控',
          entities: ['CreditLimit', 'RiskLevel', 'Blacklist'],
          actions: ['设置信用额度', '评估风险等级', '管理黑名单']
        }
      ]
    },
    {
      id: 'shipment-tracking',
      name: '运输跟踪流程',
      description: '从下单到交付的全程跟踪管理',
      category: '运输管理',
      status: 'draft',
      steps: [
        {
          id: 'step1',
          name: '运输单创建',
          entities: ['Shipment', 'ShipmentNo', 'Route'],
          actions: ['生成运输单号', '关联订单', '确认路线'],
          required: true
        },
        {
          id: 'step2',
          name: '装货操作',
          entities: ['Container', 'LoadingPlan', 'PackingList'],
          actions: ['分配集装箱', '制定装箱计划', '生成装箱单']
        },
        {
          id: 'step3',
          name: '运输监控',
          entities: ['TrackingEvent', 'Location', 'Status'],
          actions: ['更新位置', '记录事件', '推送通知']
        },
        {
          id: 'step4',
          name: '交付确认',
          entities: ['DeliveryOrder', 'ProofOfDelivery', 'Invoice'],
          actions: ['生成交付单', '确认签收', '开具发票']
        }
      ]
    }
  ];

  // 加载流程数据
  useEffect(() => {
    const savedProcesses = localStorage.getItem('businessProcesses');
    if (savedProcesses) {
      setProcesses(JSON.parse(savedProcesses));
    } else {
      setProcesses(defaultProcesses);
    }
  }, []);

  // 保存流程数据
  const saveProcesses = (newProcesses: BusinessProcess[]) => {
    setProcesses(newProcesses);
    localStorage.setItem('businessProcesses', JSON.stringify(newProcesses));
  };

  // 创建或编辑流程
  const handleSaveProcess = (values: any) => {
    const timestamp = new Date().toISOString();
    
    if (editingProcess) {
      // 编辑现有流程
      const updatedProcesses = processes.map(p => 
        p.id === editingProcess.id 
          ? { ...p, ...values, updatedAt: timestamp }
          : p
      );
      saveProcesses(updatedProcesses);
      message.success('流程更新成功');
    } else {
      // 创建新流程
      const newProcess: BusinessProcess = {
        ...values,
        id: `process-${Date.now()}`,
        steps: [],
        status: 'draft',
        createdAt: timestamp,
        updatedAt: timestamp
      };
      saveProcesses([...processes, newProcess]);
      message.success('流程创建成功');
    }
    
    setIsModalVisible(false);
    setEditingProcess(null);
    form.resetFields();
  };

  // 删除流程
  const handleDeleteProcess = (processId: string) => {
    const updatedProcesses = processes.filter(p => p.id !== processId);
    saveProcesses(updatedProcesses);
    message.success('流程删除成功');
  };

  // 复制流程
  const handleCopyProcess = (process: BusinessProcess) => {
    const newProcess: BusinessProcess = {
      ...process,
      id: `process-${Date.now()}`,
      name: `${process.name} (副本)`,
      status: 'draft',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    saveProcesses([...processes, newProcess]);
    message.success('流程复制成功');
  };

  // 导出流程到交互式设计教程
  const handleExportToTutorial = (process: BusinessProcess) => {
    // 存储到localStorage，供InteractiveDesignTutorial使用
    localStorage.setItem('selectedBusinessProcess', JSON.stringify(process));
    message.success('流程已导出，请在交互式设计教程中查看');
  };

  // 智能提取实体
  const handleExtractEntities = async () => {
    const stepName = stepForm.getFieldValue('name');
    const actions = stepForm.getFieldValue('actions');
    
    if (!stepName || !actions) {
      message.warning('请先填写步骤名称和业务动作');
      return;
    }
    
    setIsExtracting(true);
    
    try {
      const actionList = actions.split(',').map((a: string) => a.trim()).filter(Boolean);
      
      const response = await fetch(`${getApiBaseUrl()}/extract-entities`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          stepName,
          actions: actionList
        })
      });
      
      if (!response.ok) {
        throw new Error('提取失败');
      }
      
      const result = await response.json();
      
      if (result.entities && result.entities.length > 0) {
        // 将提取的实体设置到表单中
        const entityNames = result.entities.map((e: any) => e.name).join(', ');
        stepForm.setFieldsValue({ entities: entityNames });
        
        // 显示提取结果
        Modal.info({
          title: '智能提取结果',
          content: (
            <div>
              <p>根据您的业务动作，建议涉及以下实体：</p>
              <ul>
                {result.entities.map((entity: any, index: number) => (
                  <li key={index}>
                    <strong>{entity.name}</strong> - {entity.displayName}
                  </li>
                ))}
              </ul>
              <p style={{ marginTop: 16, color: '#666' }}>
                您可以根据实际需要修改或补充这些实体。
              </p>
            </div>
          ),
          width: 500
        });
      } else {
        message.info('未能提取到相关实体，请手动输入');
      }
    } catch (error) {
      console.error('实体提取错误:', error);
      message.error('智能提取失败，请手动输入实体');
    } finally {
      setIsExtracting(false);
    }
  };

  // 添加或编辑步骤
  const handleSaveStep = (values: any) => {
    if (!selectedProcess) return;
    
    const newStep: ProcessStep = {
      id: editingStep?.id || `step-${Date.now()}`,
      ...values,
      entities: values.entities.split(',').map((e: string) => e.trim()).filter(Boolean),
      actions: values.actions.split(',').map((a: string) => a.trim()).filter(Boolean)
    };
    
    let updatedSteps;
    if (editingStep) {
      updatedSteps = selectedProcess.steps.map(s => 
        s.id === editingStep.id ? newStep : s
      );
    } else {
      updatedSteps = [...selectedProcess.steps, newStep];
    }
    
    const updatedProcess = {
      ...selectedProcess,
      steps: updatedSteps,
      updatedAt: new Date().toISOString()
    };
    
    const updatedProcesses = processes.map(p => 
      p.id === selectedProcess.id ? updatedProcess : p
    );
    
    saveProcesses(updatedProcesses);
    setSelectedProcess(updatedProcess);
    setIsStepModalVisible(false);
    setEditingStep(null);
    stepForm.resetFields();
    message.success(editingStep ? '步骤更新成功' : '步骤添加成功');
  };

  // 删除步骤
  const handleDeleteStep = (stepId: string) => {
    if (!selectedProcess) return;
    
    const updatedSteps = selectedProcess.steps.filter(s => s.id !== stepId);
    const updatedProcess = {
      ...selectedProcess,
      steps: updatedSteps,
      updatedAt: new Date().toISOString()
    };
    
    const updatedProcesses = processes.map(p => 
      p.id === selectedProcess.id ? updatedProcess : p
    );
    
    saveProcesses(updatedProcesses);
    setSelectedProcess(updatedProcess);
    message.success('步骤删除成功');
  };

  // 流程卡片
  const ProcessCard = ({ process }: { process: BusinessProcess }) => (
    <Card
      hoverable
      style={{ height: '100%' }}
      actions={[
        <Tooltip title="编辑流程">
          <EditOutlined onClick={() => {
            setEditingProcess(process);
            form.setFieldsValue(process);
            setIsModalVisible(true);
          }} />
        </Tooltip>,
        <Tooltip title="复制流程">
          <CopyOutlined onClick={() => handleCopyProcess(process)} />
        </Tooltip>,
        <Tooltip title="导出到教程">
          <ExportOutlined onClick={() => handleExportToTutorial(process)} />
        </Tooltip>,
        <Popconfirm
          title="确定删除该流程吗？"
          onConfirm={() => handleDeleteProcess(process.id)}
        >
          <Tooltip title="删除流程">
            <DeleteOutlined />
          </Tooltip>
        </Popconfirm>
      ]}
      onClick={() => setSelectedProcess(process)}
    >
      <Card.Meta
        title={
          <Space>
            {process.name}
            <Badge 
              status={
                process.status === 'active' ? 'success' : 
                process.status === 'draft' ? 'warning' : 'default'
              } 
              text={
                process.status === 'active' ? '已启用' : 
                process.status === 'draft' ? '草稿' : '已归档'
              }
            />
          </Space>
        }
        description={
          <>
            <Paragraph ellipsis={{ rows: 2 }}>{process.description}</Paragraph>
            <Space wrap style={{ marginTop: 8 }}>
              <Tag color="blue">{process.category || '未分类'}</Tag>
              <Tag>{process.steps.length} 个步骤</Tag>
            </Space>
          </>
        }
      />
    </Card>
  );

  // 步骤表格列定义
  const stepColumns = [
    {
      title: '步骤名称',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: ProcessStep) => (
        <Space>
          {text}
          {record.required && <Tag color="red">必需</Tag>}
        </Space>
      )
    },
    {
      title: '涉及实体',
      dataIndex: 'entities',
      key: 'entities',
      render: (entities: string[]) => (
        <Space wrap>
          {entities.map(entity => (
            <Tag key={entity} color="blue">{entity}</Tag>
          ))}
        </Space>
      )
    },
    {
      title: '业务动作',
      dataIndex: 'actions',
      key: 'actions',
      render: (actions: string[]) => (
        <Space wrap>
          {actions.map(action => (
            <Tag key={action} color="green">{action}</Tag>
          ))}
        </Space>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_: any, record: ProcessStep) => (
        <Space>
          <Button
            type="link"
            size="small"
            onClick={() => {
              setEditingStep(record);
              stepForm.setFieldsValue({
                ...record,
                entities: record.entities.join(', '),
                actions: record.actions.join(', ')
              });
              setIsStepModalVisible(true);
            }}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除该步骤吗？"
            onConfirm={() => handleDeleteStep(record.id)}
          >
            <Button type="link" size="small" danger>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Title level={2}>业务流程管理</Title>
      <Paragraph>
        管理和配置业务流程，这些流程可以导出到交互式设计教程中使用
      </Paragraph>

      <Card
        title={
          <Row justify="space-between" align="middle">
            <Col>
              <Space>
                <Text strong>流程列表</Text>
                <Badge count={processes.length} showZero />
              </Space>
            </Col>
            <Col>
              <Space>
                <Button
                  icon={viewMode === 'grid' ? <UnorderedListOutlined /> : <AppstoreOutlined />}
                  onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                >
                  {viewMode === 'grid' ? '列表视图' : '网格视图'}
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setEditingProcess(null);
                    form.resetFields();
                    setIsModalVisible(true);
                  }}
                >
                  新建流程
                </Button>
              </Space>
            </Col>
          </Row>
        }
      >
        {viewMode === 'grid' ? (
          <Row gutter={[16, 16]}>
            {processes.map(process => (
              <Col key={process.id} xs={24} sm={12} lg={8}>
                <ProcessCard process={process} />
              </Col>
            ))}
          </Row>
        ) : (
          <List
            dataSource={processes}
            renderItem={process => (
              <List.Item
                key={process.id}
                actions={[
                  <Button
                    type="link"
                    onClick={() => setSelectedProcess(process)}
                  >
                    查看详情
                  </Button>,
                  <Button
                    type="link"
                    onClick={() => {
                      setEditingProcess(process);
                      form.setFieldsValue(process);
                      setIsModalVisible(true);
                    }}
                  >
                    编辑
                  </Button>,
                  <Button
                    type="link"
                    onClick={() => handleExportToTutorial(process)}
                  >
                    导出
                  </Button>
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      {process.name}
                      <Badge 
                        status={
                          process.status === 'active' ? 'success' : 
                          process.status === 'draft' ? 'warning' : 'default'
                        } 
                      />
                    </Space>
                  }
                  description={process.description}
                />
                <Space>
                  <Tag color="blue">{process.category || '未分类'}</Tag>
                  <Tag>{process.steps.length} 个步骤</Tag>
                </Space>
              </List.Item>
            )}
          />
        )}
      </Card>

      {/* 流程详情 */}
      {selectedProcess && (
        <Card
          title={
            <Row justify="space-between" align="middle">
              <Col>
                <Space>
                  <Text strong>{selectedProcess.name}</Text>
                  <Tag color="blue">{selectedProcess.category || '未分类'}</Tag>
                </Space>
              </Col>
              <Col>
                <Space>
                  <Button
                    icon={<PlayCircleOutlined />}
                    onClick={() => handleExportToTutorial(selectedProcess)}
                  >
                    在教程中使用
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setEditingStep(null);
                      stepForm.resetFields();
                      setIsStepModalVisible(true);
                    }}
                  >
                    添加步骤
                  </Button>
                </Space>
              </Col>
            </Row>
          }
          style={{ marginTop: 16 }}
        >
          <Paragraph>{selectedProcess.description}</Paragraph>
          <Table
            dataSource={selectedProcess.steps}
            columns={stepColumns}
            rowKey="id"
            pagination={false}
          />
        </Card>
      )}

      {/* 流程编辑模态框 */}
      <Modal
        title={editingProcess ? '编辑流程' : '新建流程'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingProcess(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveProcess}
        >
          <Form.Item
            name="name"
            label="流程名称"
            rules={[{ required: true, message: '请输入流程名称' }]}
          >
            <Input placeholder="如：创建订单流程" />
          </Form.Item>
          
          <Form.Item
            name="description"
            label="流程描述"
            rules={[{ required: true, message: '请输入流程描述' }]}
          >
            <TextArea rows={3} placeholder="描述该流程的目的和主要功能" />
          </Form.Item>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="category"
                label="流程分类"
              >
                <Select placeholder="选择分类">
                  <Option value="订单管理">订单管理</Option>
                  <Option value="主数据管理">主数据管理</Option>
                  <Option value="运输管理">运输管理</Option>
                  <Option value="财务管理">财务管理</Option>
                  <Option value="其他">其他</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="流程状态"
                initialValue="draft"
              >
                <Select>
                  <Option value="active">已启用</Option>
                  <Option value="draft">草稿</Option>
                  <Option value="archived">已归档</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingProcess ? '更新' : '创建'}
              </Button>
              <Button onClick={() => {
                setIsModalVisible(false);
                setEditingProcess(null);
                form.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 步骤编辑模态框 */}
      <Modal
        title={editingStep ? '编辑步骤' : '添加步骤'}
        open={isStepModalVisible}
        onCancel={() => {
          setIsStepModalVisible(false);
          setEditingStep(null);
          stepForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={stepForm}
          layout="vertical"
          onFinish={handleSaveStep}
        >
          <Form.Item
            name="name"
            label="步骤名称"
            rules={[{ required: true, message: '请输入步骤名称' }]}
          >
            <Input placeholder="如：客户需求确认" />
          </Form.Item>
          
          <Form.Item
            name="description"
            label="步骤描述"
          >
            <TextArea rows={2} placeholder="描述该步骤的具体内容（可选）" />
          </Form.Item>
          
          <Form.Item
            label={
              <Space>
                涉及实体
                <Tooltip title="填写步骤名称和业务动作后，点击智能提取按钮可自动识别相关实体">
                  <QuestionCircleOutlined style={{ color: '#1890ff' }} />
                </Tooltip>
              </Space>
            }
            extra="多个实体用逗号分隔，如：Customer, Order, Route"
          >
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item
                name="entities"
                noStyle
                rules={[{ required: true, message: '请输入涉及的实体' }]}
              >
                <TextArea 
                  rows={2} 
                  placeholder="Customer, Contact, ServiceRequirement"
                  style={{ width: 'calc(100% - 100px)' }}
                />
              </Form.Item>
              <Button
                type="default"
                icon={isExtracting ? <LoadingOutlined /> : <BulbOutlined />}
                onClick={handleExtractEntities}
                disabled={isExtracting}
                style={{ height: 'auto' }}
              >
                {isExtracting ? '提取中' : '智能提取'}
              </Button>
            </Space.Compact>
          </Form.Item>
          
          <Form.Item
            name="actions"
            label="业务动作"
            rules={[{ required: true, message: '请输入业务动作' }]}
            extra="多个动作用逗号分隔，如：选择客户, 确认联系人, 记录需求"
          >
            <TextArea rows={2} placeholder="选择客户, 确认联系人, 记录需求" />
          </Form.Item>
          
          <Form.Item
            name="required"
            valuePropName="checked"
          >
            <Switch checkedChildren="必需步骤" unCheckedChildren="可选步骤" />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingStep ? '更新' : '添加'}
              </Button>
              <Button onClick={() => {
                setIsStepModalVisible(false);
                setEditingStep(null);
                stepForm.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BusinessProcessManager;